/* eslint-disable max-len */
import { mapGetters } from 'vuex';
import { CONTRACTS, COLOR_MAP, COLOR_INDEXES, PRICE_FACTORS } from '@/components/pages/page-election-map-new/election-map/election-map.vue';
import { getLeadingTradedContract, roundToTwo } from '@/utils/helpers';

const WIDTH_PERCENTAGE_THRESHOLD = 5;

const getWidth = value => roundToTwo(value) * 100;
const checkThreshold = (obj) => {
  const value = parseInt(obj.width.replace('%', ''), 10);
  return value >= WIDTH_PERCENTAGE_THRESHOLD;
};

const updateCounters = ({ price, counter, stateVotes }) => {
  if (price <= PRICE_FACTORS.LOW) {
    counter.low += stateVotes;
  } else if (price <= PRICE_FACTORS.MEDIUM) {
    counter.medium += stateVotes;
  } else if (price <= PRICE_FACTORS.HIGH) {
    counter.high += stateVotes;
  }
  counter.total += stateVotes;
};

export default {
  props: {
    mapStateData: {},
    type: {},
  },
  computed: {
    ...mapGetters('app', ['isLayoutDesktop', 'isLayoutMobile']),
    isSenate() {
      return this.type === 'senate';
    },
    show() {
      return Object.keys(this.mapStateData).length > 0;
    },
    barStyleModifiers() {
      // to right pad in case width does not equal 100
      let color = COLOR_MAP.RED[COLOR_INDEXES.HIGH].color;
      if (this.republican.high === 0) {
        color = COLOR_MAP.RED[COLOR_INDEXES.MEDIUM].color;
      }
      return {
        background: color,
      };
    },
    democratLabelStyleModifiers() {
      return {
        color: COLOR_MAP.BLUE[COLOR_INDEXES.HIGH].color,
      };
    },
    republicanLabelStyleModifiers() {
      return {
        color: COLOR_MAP.RED[COLOR_INDEXES.HIGH].color,
      };
    },
    democratic() {
      return Object.values(this.mapStateData).filter((i) => {
        const targetContract = this.getTargetContract(i);
        return targetContract && targetContract.contractName === CONTRACTS.DEMOCRATIC;
      });
    },
    democraticCounters() {
      const counter = {
        total: 5,
        low: 0,
        medium: 0,
        high: 5,
        predefinedReal: 0,
      };
      if (this.isSenate) {
        counter.predefined = 14; // wont be displayed, used to reserve width
        counter.predefinedReal = 36;
        // 5 demo
        // 12 repub
      }
      this.democratic.forEach((i) => {
        const targetContract = this.getTargetContract(i);
        if (!targetContract) {
          return;
        }
        const stateVotes = this.isSenate ? 1 : i.stateVotes;
        updateCounters({
          counter,
          price: targetContract.lastTradePrice,
          stateVotes,
        });
      });
      return counter;
    },
    republican() {
      return Object.values(this.mapStateData).filter((i) => {
        const targetContract = this.getTargetContract(i);
        return targetContract && targetContract.contractName === CONTRACTS.REPUBLICAN;
      });
    },
    republicanCounters() {
      const counter = {
        total: 12,
        low: 0,
        medium: 0,
        high: 12,
        predefinedReal: 0,
      };
      if (this.isSenate) {
        counter.predefined = 8; // wont be displayed, used to reserve width
        counter.predefinedReal = 29;
      }
      this.republican.forEach((i) => {
        const targetContract = this.getTargetContract(i);
        if (!targetContract) {
          return;
        }
        const stateVotes = this.isSenate ? 1 : i.stateVotes;
        updateCounters({
          counter,
          price: targetContract.lastTradePrice,
          stateVotes,
        });
      });
      return counter;
    },
    totalVotes() {
      return this.republicanCounters.total + this.democraticCounters.total;
    },
    democraticPredefinedBarSectionStyleModifiers() {
      return {
        width: `${getWidth(this.democraticCounters.predefined / this.totalVotes)}%`,
        background: COLOR_MAP.BLUE[COLOR_INDEXES.HIGH].color,
      };
    },
    democraticHighBarSectionStyleModifiers() {
      return {
        width: `${getWidth(this.democraticCounters.high / this.totalVotes)}%`,
        background: COLOR_MAP.BLUE[COLOR_INDEXES.HIGH].color,
      };
    },
    democraticMediumBarSectionStyleModifiers() {
      return {
        width: `${getWidth(this.democraticCounters.medium / this.totalVotes)}%`,
        background: COLOR_MAP.BLUE[COLOR_INDEXES.MEDIUM].color,
      };
    },
    democraticLowBarSectionStyleModifiers() {
      return {
        width: `${getWidth(this.democraticCounters.low / this.totalVotes)}%`,
        background: COLOR_MAP.BLUE[COLOR_INDEXES.LOW].color,
      };
    },
    republicanPredefinedBarSectionStyleModifiers() {
      return {
        width: `${getWidth(this.republicanCounters.predefined / this.totalVotes)}%`,
        background: COLOR_MAP.RED[COLOR_INDEXES.HIGH].color,
      };
    },
    republicanHighBarSectionStyleModifiers() {
      return {
        width: `${getWidth(this.republicanCounters.high / this.totalVotes)}%`,
        background: COLOR_MAP.RED[COLOR_INDEXES.HIGH].color,
      };
    },
    republicanMediumBarSectionStyleModifiers() {
      return {
        width: `${getWidth(this.republicanCounters.medium / this.totalVotes)}%`,
        background: COLOR_MAP.RED[COLOR_INDEXES.MEDIUM].color,
      };
    },
    republicanLowBarSectionStyleModifiers() {
      return {
        width: `${getWidth(this.republicanCounters.low / this.totalVotes)}%`,
        background: COLOR_MAP.RED[COLOR_INDEXES.LOW].color,
      };
    },
    showDemocraticHighText() {
      return this.democraticCounters.high > 0 && checkThreshold(this.democraticHighBarSectionStyleModifiers);
    },
    showDemocraticMediumText() {
      return this.democraticCounters.medium > 0 && checkThreshold(this.democraticMediumBarSectionStyleModifiers);
    },
    showDemocraticLowText() {
      return this.democraticCounters.low > 0 && checkThreshold(this.democraticLowBarSectionStyleModifiers);
    },
    showRepublicanHighText() {
      return this.republicanCounters.high > 0 && checkThreshold(this.republicanHighBarSectionStyleModifiers);
    },
    showRepublicanMediumText() {
      return this.republicanCounters.medium > 0 && checkThreshold(this.republicanMediumBarSectionStyleModifiers);
    },
    showRepublicanLowText() {
      return this.republicanCounters.low > 0 && checkThreshold(this.republicanLowBarSectionStyleModifiers);
    },
    showDemocraticPredefinedText() {
      return true;
    },
    showRepublicanPredefinedText() {
      return true;
    },
    majorityText() {
      if (this.isSenate) {
        return this.isLayoutDesktop ? '50 Seats' : '50';
      }
      return 'Majority';
    },
  },
  methods: {
    getTargetContract(data) {
      return getLeadingTradedContract((data && data.contracts) || []);
    },
  },
};



// WEBPACK FOOTER //
// ./src/components/pages/page-election-map-new/election-map-toolbar/election-map-toolbar.js